import { getEnv } from "@/util/env";
import { capitalizeFirstLetter, getNotEmpty } from "@/util/text";
import { t } from "i18next";

const delimiter = `|`;
const delimiterSpaced = ` | `;
const delimiterSpace = ` `;

export class PageMeta {
    domain = window.location.host.replace('www.', '');
    
    getBusinessName() {
        const name = this.domain.split(".")[0];

        return name.charAt(0).toUpperCase() + name.slice(1);
    }

    update(values: { title: string, description: string }) {
        document.title = values.title;
        document.querySelector('meta[name="description"]')?.setAttribute('content', values.description);
    }

    updateToDefault() {
        this.update({
            title: this.createHomePageTitle(),
            description: this.createHomePageDescription(),
        });
    }

    createProductTitle(params: { mpn: string, productName: string, brandName: string }) {
        const segments = [];
        const values = [
            params.mpn,
            params.productName,
            params.brandName,
        ];

        values.forEach((value) => {
            const trimmed = value?.trim();
            if (trimmed?.length) {
                segments.push(value);
            }
        });

        segments.push(delimiter);
        segments.push(this.domain);

        return segments.join(delimiterSpace);
    }

    createProductDescription(params: { productName: string, brandName: string, productDesctiption?: string }) {
        const segments = [];
        const brand = params.brandName;
        const product = params.productName;
        const distributor = this.getBusinessName();
        const description = getNotEmpty(params.productDesctiption);

        segments.push(
            t(`35c145817a3d1a20.product-page-description-1`, `Buy ${brand} ${product} at ${distributor}.`, { brand, product, distributor }),
        );

        if (description) {
            segments.push(description);
        }

        return segments.join(delimiterSpace);
    }

    createCategoryTitle(params: { categoryName: string; currentPage: number | null }) {
        const { categoryName, currentPage } = params;
        const categoryCapitalized = capitalizeFirstLetter(categoryName);

        const parts: string[] = [
            t(`8107a67d2b5ed5b5.category-page-title-1`, `Buy ${categoryName} Online`, { category: categoryName }),
        ];

        if (currentPage && currentPage > 1) {
            parts.push(t('8107a67d2b5ed5b5.category-page-title-3', `Page ${currentPage}`, { currentPage }));
        }

        parts.push(
            this.getBusinessName(),
            t(`85ab638765ee9be3.category-page-title-2`, `${categoryCapitalized} Parts`, {
                category: categoryCapitalized,
            })
        );

        return parts.join(delimiterSpaced);
    }

    createCategoryDescription(params: { categoryName: string }) {
        const category = params.categoryName;
        const distributor = this.getBusinessName();

        return t('0840f1e4da189ea5.category-page-description', `Explore our selection of ${category} at ${distributor}.`, { category, distributor });
    }

    createBrandTitle(params: { brandName: string; currentPage: number | null }) {
        const { brandName, currentPage } = params;
        const distributor = this.getBusinessName();

        const parts: string[] = [
            t('f23f67fd04986b46.brand-page-title-1', `${brandName} Auto Parts`, { brand: brandName }),
        ];

        if (currentPage && currentPage > 1) {
            parts.push(t('8107a67d2b5ed5b5.category-page-title-3', `Page ${currentPage}`, { currentPage }));
        }

        parts.push(
            t(
                '979873898d2d15b8.brand-page-title-2',
                `Shop ${brandName} Car Parts with ${distributor}`,
                { brand: brandName, distributor }
            )
        );

        return parts.join(delimiterSpaced);
    }

    createBrandDescription(params: { brandName: string }) {
        const brand = params.brandName;
        const distributor = this.getBusinessName();

        return [
            t('2a286df26f6b0cce.brand-page-description-1', `Discover ${brand} auto parts with ${distributor}.`, { brand, distributor }),
            t('f5a186580d1ca0c7.brand-page-description-2', `High-quality car parts and accessories, from brakes to wiper blades, designed for durability and performance. Shop now for great deals!`)
        ].join(' ');
    }

    createHomePageTitle() {
        const domain = capitalizeFirstLetter(this.domain);

        return t('c1e4422974e973e2.home-page-title', `${domain}: Automotive spare parts wholesale and retail trade`, { domain });
    }

    createHomePageDescription() {
        return t('de5830635510b692.home-page-description', `Wholesale of spare parts for trucks and cars. Order online, fast shipping, low prices`);
    }
}